import {
  doc,
  query,
  where,
  getDocs,
  updateDoc,
  collection,
  increment,
} from "firebase/firestore";
import { User } from "@/context/UserContext";

import { httpsCallable } from "firebase/functions";
import {
  signInWithCustomToken,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { db, functions, auth as firebaseAuth } from "@/config/firebase";
import { getDoc, setDoc } from "firebase/firestore";
import { getTelegramId, getTelegramStartAppParam } from "@/utils/telegram";

const usersCollection = collection(db, "users");

const getCustomTokenByWalletCallable = httpsCallable(
  functions,
  "getCustomTokenByWallet"
);
const createUserOnlyCallable = httpsCallable(
  functions,
  "createUserOnly"
);

export const authenticateUserWithoutSignature = async (
  username: string,
  walletId: string
): Promise<{ success: boolean; user?: User; error?: string }> => {
  try {
    const telegramId = getTelegramId();
    const referralStartParam = getTelegramStartAppParam();
    // 1. Try to get a token for this wallet (Login)
    try {
      const getTokenResp = (await getCustomTokenByWalletCallable({
        walletAddress: walletId,
        telegramId: telegramId,
        referralStartParam
      })) as any;

      // If successful, sign in
      await signInWithCustomToken(firebaseAuth, getTokenResp?.data.customToken);

      // Fetch user data to return
      const userRef = doc(db, "users", getTokenResp.data.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        // userSnap.id should match getTokenResp.data.uid
        return {
          success: true,
          user: {
            uid: userSnap.id,
            username: data.username,
            walletId: data.walletId,
            points: data.points,
            bananium: data.bananium,
            token404: data.token404,
            lastBananiumClaimTimestamp: data?.lastBananiumClaimTimestamp,
            virtualWalletId: data.virtualWalletId,
            telegramId: data.telegramId,
            referralCount: data.referralCount,
            referralRewardsEarned: data.referralRewardsEarned,
          },
        };
      }
    } catch (loginErr: any) {
      // If error is NOT "not-found", then it's a real error (e.g. network)
      if (loginErr.code !== 'functions/not-found' && loginErr.message !== 'User not found. Please sign up.') {
        throw loginErr;
      }
      // If "not-found", proceed to registration (Accessing "catch" block implies we need to register)
    }

    // 2. Registration Flow (User not found)
    // Call createUserOnly. It will now check if username is taken on the backend.

    const createResp = (await createUserOnlyCallable({
      walletAddress: walletId,
      username: username,
      telegramId: telegramId,
      referralStartParam,
    })) as CreateUserWithWalletCallable;

    await signInWithCustomToken(firebaseAuth, createResp?.data.customToken);

    // Trigger virtual wallet creation
    await createVirtualWalletCallable();

    return {
      success: true,
      user: {
        uid: createResp.data.uid,
        username,
        walletId,
        points: 0,
        bananium: 0,
        token404: 0,
        lastBananiumClaimTimestamp: Date.now(),
        telegramId: telegramId,
        referralCount: 0,
        referralRewardsEarned: 0,
      },
    };

  } catch (err: any) {
    console.error("Auth Error:", err);
    // Map common errors to user-friendly messages
    if (err.message.includes("Username already taken")) {
      return { success: false, error: "Username already taken." };
    }
    if (err.message.includes("Wallet already linked")) {
      return { success: false, error: "This wallet is already linked to another account." };
    }

    return {
      success: false,
      error: err?.message || "Unexpected error during authentication.",
    };
  }
};

const createUserWithWalletCallable = httpsCallable(
  functions,
  "createUserWithWallet"
);
const createVirtualWalletCallable = httpsCallable(
  functions,
  "createVirtualWallet"
);
const authTelegramCallable = httpsCallable(
  functions,
  "authTelegram"
);

type GetCustomTokenByWalletCallable = {
  data: { customToken: string; uid: string };
};
type CreateUserWithWalletCallable = {
  data: { customToken: string; uid: string };
};

export const checkExistence = async (field: string, value: string) => {
  const q = query(usersCollection, where(field, "==", value));
  try {
    const snapshot = await getDocs(q);
    return { success: true, exists: !snapshot.empty, snapshot };
  } catch (error) {
    console.error(`[Firebase] Error checking ${field}:`, error);
    return {
      success: false,
      error: `Unable to verify "${field}". Check your network and try again.`,
    };
  }
};







export const signInWithGoogle = async () => {
  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({ prompt: "select_account" });
  const isTelegramWebApp = Boolean((window as any)?.Telegram?.WebApp);
  const ua = navigator.userAgent || "";
  const isInAppWebView =
    /Telegram|FBAN|FBAV|Instagram|Line\/|wv\)/i.test(ua) ||
    (/\b(iPhone|iPad|iPod)\b/i.test(ua) && /\bAppleWebKit\b/i.test(ua) && !/\bSafari\b/i.test(ua));

  const ensureGoogleUserProfile = async (user: any) => {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const telegramId = getTelegramId();
      const referralStartParam = getTelegramStartAppParam();
      await setDoc(userRef, {
        username: user.displayName || user.email?.split('@')[0] || "GigaPlayer",
        points: 0,
        bananium: 0,
        token404: 0,
        createdAt: new Date(),
        role: "user",
        telegramId: telegramId,
        referralStartParam: referralStartParam || null
      });
      // Also trigger virtual wallet creation
      await createVirtualWalletCallable();
    }
  };

  try {
    // Popup flows are unreliable in embedded webviews due to COOP/window.closed restrictions.
    if (isTelegramWebApp || isInAppWebView) {
      await signInWithRedirect(firebaseAuth, googleProvider);
      return { success: true, redirect: true };
    }

    const result = await signInWithPopup(firebaseAuth, googleProvider);
    await ensureGoogleUserProfile(result.user);
    return { success: true, uid: result.user.uid };
  } catch (error: any) {
    const popupBlockedCodes = new Set([
      "auth/popup-blocked",
      "auth/popup-closed-by-user",
      "auth/cancelled-popup-request",
      "auth/operation-not-supported-in-this-environment",
    ]);

    if (popupBlockedCodes.has(error?.code)) {
      await signInWithRedirect(firebaseAuth, googleProvider);
      return { success: true, redirect: true };
    }

    console.error("[Google Auth] Error:", error);
    return { success: false, error: error.message };
  }
};

export const completeGoogleRedirectSignIn = async () => {
  try {
    const result = await getRedirectResult(firebaseAuth);
    if (!result?.user) return { success: false, skipped: true };

    const user = result.user;
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      const telegramId = getTelegramId();
      const referralStartParam = getTelegramStartAppParam();
      await setDoc(userRef, {
        username: user.displayName || user.email?.split('@')[0] || "GigaPlayer",
        points: 0,
        bananium: 0,
        token404: 0,
        createdAt: new Date(),
        role: "user",
        telegramId: telegramId,
        referralStartParam: referralStartParam || null
      });
      await createVirtualWalletCallable();
    }

    return { success: true, uid: user.uid };
  } catch (error: any) {
    console.error("[Google Redirect Auth] Error:", error);
    return { success: false, error: error.message };
  }
};

export const signInWithTelegram = async (telegramData: any) => {
  try {
    const result: any = await authTelegramCallable(telegramData);
    if (result.data?.customToken) {
      await signInWithCustomToken(firebaseAuth, result.data.customToken);
      // Trigger virtual wallet creation if needed
      await createVirtualWalletCallable();
      return { success: true };
    }
    throw new Error("Failed to get custom token from Telegram auth");
  } catch (error: any) {
    console.error("[Telegram Auth] Error:", error);
    return { success: false, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    await firebaseAuth.signOut();
    return { success: true };
  } catch (error: any) {
    console.error("[Auth] Logout Error:", error);
    return { success: false, error: error.message };
  }
};
